import { useRef, useState } from "react";
import {
  ReviewItem,
  ReviewItemStatus,
  KIND_LABEL,
  SEVERITY_COLOR,
} from "../lib/reviewItems";
import {
  setSpanAttributionStatus,
  appendSpanAttribution,
  removeSpanAttribution,
  buildManualSpan,
} from "../lib/corrections";
import { supabase } from "../lib/supabase";
import { formatWallClock } from "../lib/timestamps";
import { CorrectionPopover } from "./CorrectionPopover";

interface Props {
  item: ReviewItem;
  recordedAt: string | null;
  isLocked: boolean;
  meetingId: string;
  workspaceId: string;
  onSeek: (ms: number) => void;
  // Free-form text extractor for the Correct popover (sentinel kinds).
  // Same shape as the prior FlagPanel's getOriginalText.
  getOriginalText: (startMs: number, endMs: number) => string;
  // Edit affordance for claude_speaker proposals: seek to the entry's
  // start_ms AND request the parent open the inline SpeakerOverride at
  // that turn. The parent owns the editingTurnIdx state on TranscriptView
  // and decides which turn matches the timestamp.
  onEditSpeaker?: (startMs: number) => void;
  // Fired after an apply/undo span write succeeds, so the parent can
  // force a refresh instead of waiting for the unreliable Realtime echo.
  // The parent's reload re-aggregates item.appliedSpan, flipping the row
  // to/from the "Applied" pill. Mirrors onExitSplitAfterSave's refresh.
  onActed?: () => void | Promise<void>;
}

// One row of the unified ReviewPanel. Severity dot + click-to-seek
// timestamp + plain-language kind label + detail + evidence toggle +
// per-kind action buttons. Inherits the FlagRow visual vocabulary
// (DECISIONS.md 2026-05-13 "Flag panel UI" entry) and extends it with
// the kind-aware act-or-dismiss model (2026-06-06).
//
// Status persistence is per-source:
//   sentinel       -> flags.status column (UPDATE on flags row)
//   claude_speaker -> transcripts.span_attributions jsonb (read-modify-
//                     write via setSpanAttributionStatus)
//
// Apply path (suggestedName != null): builds a manual SpanAttribution
// and appends it, then fires onActed so the parent force-reloads
// span_attributions and re-aggregates item.appliedSpan on/off (Realtime
// is unreliable here, so we don't wait for the echo).
//
// Two write disciplines coexist:
//   - Status changes (dismiss / undo-to-pending): optimistic update +
//     revert-on-error per the project-wide SPA-write principle. The
//     optimisticStatus state flips immediately and reverts if the write
//     throws; Realtime later confirms.
//   - Apply / undo (span_attributions writes): NO optimistic update. The
//     derived appliedSpan follows the reloaded data, so we force a reload
//     via onActed after the write resolves and let the fresh props flip
//     the row to/from the "Applied" pill.
export function ReviewItemRow({
  item,
  recordedAt,
  isLocked,
  meetingId,
  workspaceId,
  onSeek,
  getOriginalText,
  onEditSpeaker,
  onActed,
}: Props) {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState<ReviewItemStatus>(item.status);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [correctOpen, setCorrectOpen] = useState(false);
  // Guards the sync-from-props path from reverting the optimistic UI in
  // the window between setWriting(false) and the Realtime echo landing.
  const pendingWriteRef = useRef(false);

  // (a) Clear the pending flag once Realtime delivers the matching status.
  if (pendingWriteRef.current && item.status === optimisticStatus) {
    pendingWriteRef.current = false;
  }
  // (b) Sync from props only when no local write is pending (cross-tab
  // change, re-annotation forcing a new item.status, etc.).
  if (!writing && !pendingWriteRef.current && optimisticStatus !== item.status) {
    setOptimisticStatus(item.status);
  }

  const status: ReviewItemStatus = optimisticStatus;
  const isDismissed = status === "dismissed";

  const sevDot = SEVERITY_COLOR[item.severity];
  const ts = item.start_ms != null ? formatWallClock(recordedAt, item.start_ms) : null;

  async function persistStatus(next: ReviewItemStatus): Promise<void> {
    const prev = status;
    pendingWriteRef.current = true;
    setOptimisticStatus(next);
    setWriting(true);
    setError(null);
    try {
      if (item.origin.source === "sentinel") {
        const { error: err } = await supabase
          .from("flags")
          .update({ status: next })
          .eq("id", item.origin.flag.id);
        if (err) throw err;
      } else if (item.origin.source === "claude_speaker") {
        // Translate the panel's status vocabulary into what the
        // span_attribution jsonb stores (pending|accepted|dismissed --
        // no "corrected" for L3 entries). Only "dismissed" and "pending"
        // are produced by the act-or-dismiss model for claude_speaker rows.
        const mapped: "pending" | "accepted" | "dismissed" =
          next === "dismissed" ? "dismissed" : "pending";
        await setSpanAttributionStatus(meetingId, item.origin.entry.id, mapped);
      }
    } catch (err) {
      pendingWriteRef.current = false;
      setOptimisticStatus(prev);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWriting(false);
    }
  }

  // Apply the suggested name by building a manual span and appending it,
  // then force a refresh via onActed so the parent reloads
  // span_attributions and re-aggregates item.appliedSpan, flipping the row
  // to the applied-pill state. No optimistic status mutation here — the
  // appliedSpan prop arriving via the forced reload is the source of truth.
  // onActed runs only after the write resolves (inside try), so a thrown
  // write skips the refresh.
  async function applySuggestion(): Promise<void> {
    if (item.suggestedName == null || item.start_ms == null || item.end_ms == null) return;
    setWriting(true);
    setError(null);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const span = buildManualSpan({
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        createdBy: userData.user?.id ?? null,
        startMs: item.start_ms,
        endMs: item.end_ms,
        name: item.suggestedName,
        rawLabel: item.rawLabelAtCreation ?? "",
      });
      await appendSpanAttribution(meetingId, span);
      await onActed?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWriting(false);
    }
  }

  // Remove the applied manual span (undo path for the applied-pill state),
  // then force a refresh via onActed. onActed runs only after the write
  // resolves, so a thrown removal skips the refresh.
  async function undoApply(): Promise<void> {
    if (!item.appliedSpan) return;
    setWriting(true);
    setError(null);
    try {
      await removeSpanAttribution(meetingId, item.appliedSpan.id);
      await onActed?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWriting(false);
    }
  }

  return (
    <li
      style={{
        padding: "8px 0",
        borderTop: "1px solid var(--border, #374151)",
        opacity: isDismissed ? 0.5 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span
          aria-label={`severity ${item.severity}`}
          title={`severity: ${item.severity}`}
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: 4,
            background: sevDot,
            flexShrink: 0,
          }}
        />
        {ts && item.start_ms != null ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSeek(item.start_ms!);
            }}
            title="Jump to this moment in the audio"
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: "var(--text, #f9fafb)",
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: 12,
              textDecoration: "underline dotted",
              flexShrink: 0,
            }}
          >
            {ts}
          </button>
        ) : (
          <span
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              color: "var(--text-muted)",
              flexShrink: 0,
            }}
          >
            —
          </span>
        )}
        <span style={{ fontSize: 13, flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontFamily: "monospace",
              color: "var(--text-muted)",
              marginRight: 6,
              fontSize: 12,
            }}
          >
            {KIND_LABEL[item.kind]}
          </span>
          <span
            style={{
              textDecoration: isDismissed ? "line-through" : undefined,
            }}
          >
            {item.detail}
          </span>
        </span>
        <RowActions
          item={item}
          status={status}
          writing={writing}
          isLocked={isLocked}
          onDismiss={() => persistStatus("dismissed")}
          onUndo={() => persistStatus("pending")}
          onUndoApply={undoApply}
          onApply={applySuggestion}
          onCorrect={() => setCorrectOpen(true)}
          onEditSpeaker={() => {
            if (item.start_ms != null) onEditSpeaker?.(item.start_ms);
          }}
        />
        <button
          type="button"
          onClick={() => setEvidenceOpen((v) => !v)}
          aria-expanded={evidenceOpen}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            color: "var(--text-muted)",
            cursor: "pointer",
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          {evidenceOpen ? "hide evidence" : "evidence"}
        </button>
      </div>
      {correctOpen && item.origin.source === "sentinel" && (
        <div style={{ margin: "8px 0 0 18px" }}>
          <CorrectionPopover
            meetingId={meetingId}
            workspaceId={workspaceId}
            originalText={getOriginalText(item.start_ms ?? 0, item.end_ms ?? 0)}
            startMs={item.start_ms ?? 0}
            endMs={item.end_ms ?? 0}
            flagId={item.origin.flag.id}
            onSaved={() => {
              // Sentinel rows: the popover writes the correction +
              // flips the flag's DB status to 'corrected' in the same
              // optimistic-update window. Mirror it here so RowActions
              // re-renders with the corrected pill before the
              // realtime echo lands.
              setOptimisticStatus("corrected");
              setCorrectOpen(false);
            }}
            onClose={() => setCorrectOpen(false)}
          />
        </div>
      )}
      {evidenceOpen && (
        <pre
          style={{
            margin: "6px 0 0 18px",
            padding: 8,
            background: "var(--surface-2, #21252f)",
            borderRadius: 6,
            fontSize: 11,
            color: "var(--text-muted)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(item.evidence, null, 2)}
        </pre>
      )}
      {error && (
        <p
          className="error"
          style={{ fontSize: 12, margin: "6px 0 0 18px" }}
        >
          Couldn't save: {error}. Click the action again to retry.
        </p>
      )}
      {status === "corrected" && (
        <p style={{ fontSize: 12, margin: "6px 0 0 18px", color: "var(--text-muted)" }}>
          Corrected via the corrections layer.
        </p>
      )}
    </li>
  );
}

function RowActions({
  item,
  status,
  writing,
  isLocked,
  onDismiss,
  onUndo,
  onUndoApply,
  onApply,
  onCorrect,
  onEditSpeaker,
}: {
  item: ReviewItem;
  status: ReviewItemStatus;
  writing: boolean;
  isLocked: boolean;
  onDismiss: () => void;
  onUndo: () => void;
  onUndoApply: () => void;
  onApply: () => void;
  onCorrect: () => void;
  onEditSpeaker: () => void;
}) {
  const containerStyle: React.CSSProperties = {
    display: "flex",
    gap: 6,
    alignItems: "center",
    flexShrink: 0,
  };

  // Terminal-pill display. Precedence (checked in order):
  //
  //   1. dismissed           → "✗ Dismissed" + undo → pending
  //   2. appliedSpan != null → "✓ Applied: <name>" + undo → remove span
  //   3. corrected           → "✎ Corrected" + undo → pending
  //   4. accepted (legacy)   → "✓ Reviewed" + undo → pending
  //   5. pending (default)   → action buttons

  if (status === "dismissed") {
    return (
      <span style={containerStyle}>
        <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }}>
          ✗ Dismissed
        </span>
        <button
          type="button"
          className="secondary"
          disabled={writing || isLocked}
          onClick={onUndo}
          title={isLocked ? "Unlock the meeting first." : "Undo; restore to pending"}
          style={{ padding: "2px 8px", fontSize: 11 }}
        >
          undo
        </button>
      </span>
    );
  }

  if (item.appliedSpan != null) {
    return (
      <span style={containerStyle}>
        <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }}>
          ✓ Applied: {item.appliedSpan.name}
        </span>
        <button
          type="button"
          className="secondary"
          disabled={writing || isLocked}
          onClick={onUndoApply}
          title={isLocked ? "Unlock the meeting first." : "Undo; remove the applied speaker attribution"}
          style={{ padding: "2px 8px", fontSize: 11 }}
        >
          undo
        </button>
      </span>
    );
  }

  if (status === "corrected") {
    return (
      <span style={containerStyle}>
        <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 3 }}>
          Corrected
        </span>
        <button
          type="button"
          className="secondary"
          disabled={writing || isLocked}
          onClick={onUndo}
          title={isLocked ? "Unlock the meeting first." : "Undo; restore to pending"}
          style={{ padding: "2px 8px", fontSize: 11 }}
        >
          undo
        </button>
      </span>
    );
  }

  if (status === "accepted") {
    // Legacy rows only — the UI no longer produces "accepted" status.
    // Safety branch so old acknowledged flags don't reappear as open work.
    return (
      <span style={containerStyle}>
        <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }}>
          ✓ Reviewed
        </span>
        <button
          type="button"
          className="secondary"
          disabled={writing || isLocked}
          onClick={onUndo}
          title={isLocked ? "Unlock the meeting first." : "Undo; restore to pending"}
          style={{ padding: "2px 8px", fontSize: 11 }}
        >
          undo
        </button>
      </span>
    );
  }

  // Pending: act-or-dismiss action matrix.
  //
  // PRIMARY button depends on the item (labels lowercase to match the
  // file's house style: "correct"/"dismiss"/"undo"):
  //   - canOneClickApply                → "use <name>" (applies the suggestion as a manual span)
  //   - speaker-kind, otherwise         → "fix speaker…" (opens inline SpeakerOverride)
  //   - text kind                       → "correct" (opens CorrectionPopover)
  //
  // Speaker-kind: attribution_second_look, role_inversion, or claude_speaker origin.
  const isSpeakerKind =
    item.kind === "attribution_second_look" ||
    item.kind === "role_inversion" ||
    item.origin.source === "claude_speaker";

  // One-click apply requires BOTH a suggested name AND a known Soniox label.
  // Without a label, the apply would write rawLabel "" and the render cascade
  // would match by time-range alone, leaking the name onto other speakers in
  // the span. In that case we fall through to "fix speaker…" so the user
  // picks the exact turn safely.
  const canOneClickApply = item.suggestedName != null && !!item.rawLabelAtCreation;

  return (
    <span style={containerStyle}>
      {canOneClickApply ? (
        <button
          type="button"
          className="secondary"
          disabled={writing || isLocked}
          onClick={onApply}
          title={
            isLocked
              ? "Unlock the meeting first."
              : `Apply "${item.suggestedName}" as the speaker for this span`
          }
          style={{ padding: "2px 8px", fontSize: 11 }}
        >
          use {item.suggestedName}
        </button>
      ) : isSpeakerKind ? (
        <button
          type="button"
          className="secondary"
          disabled={writing || isLocked}
          onClick={onEditSpeaker}
          title={
            isLocked
              ? "Unlock the meeting first."
              : "Jump to the turn so you can edit the speaker name inline"
          }
          style={{ padding: "2px 8px", fontSize: 11 }}
        >
          fix speaker…
        </button>
      ) : (
        <button
          type="button"
          className="secondary"
          disabled={writing || isLocked}
          onClick={onCorrect}
          title={
            isLocked
              ? "Unlock the meeting first."
              : "Fix the text in this span; the correction overlays at render"
          }
          style={{ padding: "2px 8px", fontSize: 11 }}
        >
          correct
        </button>
      )}
      <button
        type="button"
        className="secondary"
        disabled={writing || isLocked}
        onClick={onDismiss}
        title={isLocked ? "Unlock the meeting first." : "Mark this as not a problem"}
        style={{ padding: "2px 8px", fontSize: 11 }}
      >
        dismiss
      </button>
    </span>
  );
}
